/* eslint-disable react/no-unescaped-entities */
import { FC, Ref } from 'react';

import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ModalApi } from '@components/Modal';
import Input, { InputContainer } from '@components/Input';
import { HopiIcon, BaseCheckIcon } from '@assets/icons';
import { Col, FlexibleDiv } from '@components/formStyles';

import { HopiModalSchema } from '../schemas';
import { Button } from '../common/styled';
import PromotionModal from '../PromotionModal';

const DynamicDiv = styled.div<{ spaceBetween?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;

  ${({ spaceBetween }) => spaceBetween && 'justify-content: space-between'};

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const Content = styled.div`
  margin: 40px 0 0;
  font-size: 16px;
  line-height: 1.56;
  color: ${(p) => p.theme.palette.darkGray};
`;

const Section = styled.section`
  margin: 32px 0 40px;
  display: flex;
  button {
    height: unset;
    width: 148px;
    font-weight: bold;
  }
  column-gap: 20px;
  ${InputContainer} {
    flex: 1;
  }
`;
const Paragraph = styled.p`
  font-size: 14px;
  line-height: 1.57;
  color: ${(p) => p.theme.palette.darkGrey};
`;

const Bold = styled.span`
  font-weight: bold;
`;

const LogoWrapper = styled.div`
  width: auto;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    width: 100%;
  }
`;

const MainWrapper = styled.div`
  width: 75%;
  height: auto;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    width: 100%;
  }
`;

const HopiWrapper = styled.div`
  width: 25%;
  height: 253px;
  display: inline-flex;
  justify-content: center;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    width: 100%;
  }
`;

const ButtonWrapper = styled.div`
  margin-left: 20px;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    margin-top: 20px;
    margin-left: 0;
  }
`;

const StyledButton = styled(Button)`
  width: 250px;
  height: 60px;
`;

const StyledFlexibleDiv = styled(FlexibleDiv)`
  align-items: center;
  display: flex;
  width: 100%;
`;

const staticButtonStyle = {
  width: 147,
  height: 68,
};

const staticProps = {
  width: 674,
  title: 'Hopi Hesap Doğrulama',
  promotionTitle: 'Hopi Nedir?',
  promotionDescription:
    'Alışverişin app’i Hopi; zevklerini, alışveriş alışkanlıklarını öğrenir ve istediğin ürünlere kolayca ulaşmanı sağlar. Hiçbir yerde olmayan kampanyalarıyla alışverişlerinde sana Paracık kazandırır.',
};

const initialValues = {
  hopiCode: '',
};

export interface HopiModalFormData {
  hopiCode: string;
}

const HopiModal: FC<{
  modal?: Ref<ModalApi>;
  isVisible?: boolean;
  isValided?: boolean;
  onCancel?: () => void;
  onSubmit: (formData: HopiModalFormData) => void;
}> = ({ modal, isVisible, isValided = false, onCancel, onSubmit, ...rest }) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<HopiModalFormData>({
    defaultValues: initialValues,
    resolver: yupResolver(HopiModalSchema),
  });

  return (
    <PromotionModal
      modal={modal}
      isVisible={isVisible}
      onCancel={onCancel}
      {...staticProps}
      {...rest}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DynamicDiv>
          <Content>
            <DynamicDiv spaceBetween>
              <MainWrapper>
                <Col>
                  <LogoWrapper>
                    <HopiIcon width={80} />
                  </LogoWrapper>

                  <Section>
                    <Paragraph>
                      <Bold>Hopi</Bold>
                      'n varsa kampanyalardan yararlanmak ve
                      <Bold> Paracık</Bold>
                      'larınla alışveriş yapmak için aşağıya
                      <Bold> Hopi Kodunu gir.</Bold>
                    </Paragraph>
                  </Section>

                  <MainWrapper>
                    <Section>
                      <StyledFlexibleDiv top={-20}>
                        <DynamicDiv>
                          <Controller
                            control={control}
                            name='hopiCode'
                            render={({ field }) => (
                              <Input
                                {...field}
                                label='Hopi Kodunu Gir'
                                hasError={Boolean(errors.hopiCode)}
                              />
                            )}
                          />
                          <ButtonWrapper>
                            <StyledButton
                              style={staticButtonStyle}
                              disabled={isSubmitting}
                              outline
                              size='large'
                              htmlType='submit'
                              isValid={isValided}
                            >
                              <span>
                                {!isValided ? 'Doğrula' : 'Doğrulandı'}
                              </span>
                              <BaseCheckIcon width={24} height={24} />
                            </StyledButton>
                          </ButtonWrapper>
                        </DynamicDiv>
                      </StyledFlexibleDiv>
                    </Section>
                  </MainWrapper>
                </Col>
              </MainWrapper>

              <HopiWrapper>
                <Col>
                  <Section>
                    <img
                      width='125'
                      alt='hopi'
                      src='/static/images/hopi-phone.webp'
                    />
                  </Section>
                </Col>
              </HopiWrapper>
            </DynamicDiv>
          </Content>
        </DynamicDiv>
      </form>
    </PromotionModal>
  );
};

export default HopiModal;
